/**
 * @lodex-pro/shared/phone — US phone canonicalization + synthesized driver email.
 *
 * Quick 260814-k6a (ClickUp 86bbed5r5). `canonicalizePhone` and
 * `formatPhoneForDisplay` were previously module-private inside
 * `packages/mobile/src/screens/PhoneSignInScreen.tsx` (DEC-031 #9 / PHONE-01).
 * They are promoted here UNCHANGED so mobile, web-dispatch, and the API all
 * share ONE implementation — the admin driver-creation form and the driver login
 * screen must agree byte-for-byte on what a phone number normalizes to, because
 * `users.phone` is the lookup key for `resolve_driver_cognito_sub_by_phone`.
 * A second, subtly-different copy anywhere would silently break driver login.
 *
 * NOBODY TYPES "+1" — not on the login screen, not on the admin form (86bbed5r5
 * item 5). Every phone input accepts a plain 10-digit US number; E.164 is an
 * internal representation produced here, never a thing a human enters.
 */

/** The domain for synthesized driver emails. Single source of truth — see below. */
const SYNTHESIZED_EMAIL_DOMAIN = 'drivers.lodexpro.invalid';

/**
 * DEC-031 #9 / PHONE-01 — canonicalize user input to +1XXXXXXXXXX (E.164 US).
 * Strips leading +1, leading 1, spaces, dashes, parens.
 * Returns empty string when input doesn't normalize to a 10-digit US number.
 */
export function canonicalizePhone(raw: string): string {
  const digits = raw.replace(/[^\d]/g, '');
  const stripped = digits.startsWith('1') ? digits.slice(1) : digits;
  return stripped.length === 10 ? `+1${stripped}` : '';
}

/**
 * Task #57 — the characters a pasted or typed phone number may carry besides digits:
 * spaces, dots, dashes, parens and a "+" for the country code. Anything else (letters,
 * "ext", "#") means the text is not just a phone number.
 */
const PHONE_PUNCTUATION_ONLY = /^[\d\s().+-]*$/;

/**
 * Task #57 — the strict form of canonicalizePhone, for a field that holds ONE phone
 * number and nothing else (the tenant's "Phone on tickets", `tenants.print_phone`).
 * Returns +1XXXXXXXXXX, or '' when the text is not a 10-digit US number. Unlike
 * canonicalizePhone it also refuses text with letters or other symbols in it, so
 * "405-555-1234 ext 5" or "405abc5551234" is refused rather than read as digits.
 * canonicalizePhone itself stays lenient: driver sign-in depends on it unchanged.
 */
export function parseUsPhone(raw: string): string {
  return PHONE_PUNCTUATION_ONLY.test(raw) ? canonicalizePhone(raw) : '';
}

/**
 * Format a US number for display: "(XXX) XXX-XXXX". Takes canonical +1XXXXXXXXXX and
 * (task #57) any other text that reads cleanly as one, such as a bare "4055551234", so
 * a stray unformatted value never shows as raw digits. Anything else comes back unchanged.
 */
export function formatPhoneForDisplay(canonical: string): string {
  const e164 = parseUsPhone(canonical);
  if (e164 === '') return canonical;
  const d = e164.slice(2);
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

/**
 * Task #57 — format a phone field as the user types: "4" → "(4", "40555" → "(405) 55",
 * "4055551234" → "(405) 555-1234". A pasted "+1 405 555 1234" or "405.555.1234" lands
 * formatted too. Text that cannot become a 10-digit US number (letters, too many digits)
 * is returned exactly as typed, so the user sees what they entered and the save step
 * explains the problem, instead of the field silently dropping characters.
 */
export function formatPhoneAsTyped(raw: string): string {
  if (!PHONE_PUNCTUATION_ONLY.test(raw)) return raw;
  const digits = raw.replace(/\D/g, '');
  // A US area code never starts with 1, so a leading 1 is the country code.
  const national = digits.startsWith('1') ? digits.slice(1) : digits;
  // Only punctuation left (a backspaced "("): nothing to show.
  if (digits === '' && !raw.includes('+')) return '';
  // Nothing past "+", "1" or "+1 " yet: keep it as typed so the keystroke is not lost.
  if (national === '' || national.length > 10) return raw;
  if (national.length <= 3) return `(${national}`;
  if (national.length <= 6) return `(${national.slice(0, 3)}) ${national.slice(3)}`;
  return `(${national.slice(0, 3)}) ${national.slice(3, 6)}-${national.slice(6)}`;
}

/**
 * Synthesize a driver's email address from their canonical E.164 phone.
 *
 * A driver never types an email — they sign in by SMS OTP — but BOTH stores
 * demand one: `users.email` is NOT NULL + globally UNIQUE, and the Cognito pool
 * schema marks `email` REQUIRED (enforced at the NEW_PASSWORD_REQUIRED challenge,
 * not at AdminCreateUser, so a missing one silently bricks first login rather
 * than failing loudly at creation). This manufactures a valid, permanently
 * undeliverable address.
 *
 * Format: `<10 digits>@drivers.lodexpro.invalid` — e.g. `4054961783@…`.
 *   - `.invalid` is RFC 2606 §2-reserved for names "sure to be invalid", so it
 *     is a STANDARDS guarantee of non-deliverability. `.internal` (ICANN private
 *     use) is weaker — resolvable inside some corporate networks. A real domain
 *     would risk a live MX accepting or bouncing mail for a driver who has no
 *     inbox.
 *   - Bare 10 digits, not E.164: `+` is legal in a local part but carries
 *     plus-addressing semantics and encoding hazards in URLs and logs.
 *   - Deterministic from the phone, so global uniqueness is inherited from the
 *     existing UNIQUE `users_phone_idx` — no collision handling needed.
 *
 * VERIFIED against the real dev hauler pool (us-east-1_LbWkPDo6l) on 2026-08-14
 * before this format was locked in: AdminCreateUser accepted the `.invalid`
 * address, and USER_AUTH resolved by sub, by phone, and by this email — each
 * returning an SMS_OTP challenge. A mocked test could not have proven this
 * (DEC-164: only a check against the real environment counts).
 *
 * ⚠️ THIS VALUE IS IMMUTABLE ONCE THE USER EXISTS — DO NOT "RESYNC" IT.
 * It is derived from the phone at creation and then frozen. If a driver later
 * changes their phone number, ONLY `users.phone` and the Cognito `phone_number`
 * attribute change. This email and the Cognito Username (which for a driver is
 * the ORIGINAL phone) deliberately keep their creation-time values, because
 * Cognito usernames are immutable and a username cannot be rewritten in place.
 *
 * So a long-lived driver's Username/email WILL drift from their current phone.
 * That is expected and harmless: login runs
 *   phone -> POST /v1/auth/resolve-driver-username -> cognito_sub
 *         -> signIn({ username: sub })
 * which reads NEITHER the username nor the email. Anyone "fixing" the drift by
 * re-deriving this value, or by trying to recreate the Cognito user under a new
 * username, would break that driver's login and orphan their Cognito account.
 * If you need the mapping, `users.phone` is the live value; this is history.
 */
export function synthesizedDriverEmail(canonicalE164: string): string {
  const digits = canonicalE164.startsWith('+1') ? canonicalE164.slice(2) : canonicalE164;
  if (!/^\d{10}$/.test(digits)) {
    throw new Error(
      `synthesizedDriverEmail requires a canonical +1XXXXXXXXXX phone; got "${canonicalE164}"`,
    );
  }
  return `${digits}@${SYNTHESIZED_EMAIL_DOMAIN}`;
}
